import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, Button, Field, Alert, Spinner } from "@/components/ui";
import { Logo } from "@/components/Logo";
import { BadgeCheck } from "lucide-react";

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-surface flex flex-col items-center px-6 py-10">
      <div className="w-full max-w-md">
        <div className="mb-8">
          <Logo className="h-9" />
        </div>
        {children}
      </div>
    </div>
  );
}

export default function Activate() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState("loading"); // loading | ready | used | notfound
  const [seat, setSeat] = useState(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      if (!token) { setState("notfound"); return; }
      try { const res = await api.activationLookup(token); setSeat(res); setState("ready"); }
      catch (e) { setState(e?.status === 409 ? "used" : "notfound"); }
    })();
  }, [token]);

  const activate = async () => {
    setErr(null); setBusy(true);
    try {
      await api.activateSeat(token, { name: name.trim(), password });
      await refresh();
      navigate("/onboarding", { replace: true });
    } catch (e) { setErr(e?.detail || e?.message || "Activation failed"); }
    finally { setBusy(false); }
  };

  if (state === "loading") return <Shell><Spinner /></Shell>;

  if (state === "notfound") return (
    <Shell>
      <Card data-testid="activate-notfound">
        <h1 className="font-display text-2xl text-ink">Invitation not found</h1>
        <p className="text-sm text-subtle mt-2">The link may have expired. Ask your HR contact to resend it.</p>
      </Card>
    </Shell>
  );

  if (state === "used") return (
    <Shell>
      <Card data-testid="activate-used">
        <h1 className="font-display text-2xl text-ink">Already activated</h1>
        <p className="text-sm text-subtle mt-2">This invitation was already used. Sign in with your account instead.</p>
        <Link to="/login"><Button className="mt-4" data-testid="activate-goto-login">Sign in</Button></Link>
      </Card>
    </Shell>
  );

  return (
    <Shell>
      <div className="text-xs font-semibold text-brand uppercase tracking-wide">Employer benefit</div>
      <h1 className="font-display text-3xl text-ink mt-1">Activate your seat</h1>
      <p className="text-sm text-subtle mt-2 mb-6">
        <span className="font-semibold text-ink">{seat?.company_name}</span> has covered your KinBridge Health seat. Set your name and a password to activate — your family&apos;s subscription is billed to your employer.
      </p>
      <Card>
        <Field label="Email" data-testid="activate-email-input" value={seat?.email || ""} readOnly disabled />
        <Field label="Your name" data-testid="activate-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Alex Rivera" />
        <Field label="Choose a password" type="password" data-testid="activate-password-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" />
        <Alert testid="activate-error">{err}</Alert>
        <Button className="w-full mt-1" data-testid="activate-submit" loading={busy} onClick={activate} disabled={!name.trim() || password.length < 6}>
          <BadgeCheck className="w-4 h-4" /> Activate my seat
        </Button>
      </Card>
    </Shell>
  );
}
